<script setup>
import { computed, onMounted, ref } from 'vue';
import { useSiteForm } from '../useSiteForm.js';
import { accentParts, styleVars } from './shared.js';
import { quoteFieldList, quoteNextSteps, quoteProjectTypes } from '../quoteRequest.js';
import EmailTypoHint from '@/Components/EmailTypoHint.vue';

// The quote request as Trailhead draws it — and Backyard, Hub and
// Groundwork through their redraw of the same classes. The form in a
// card on the left, "What happens next" beside it. Project types are
// toggle chips (th-check), so they read as one decision rather than a
// list of checkboxes.
const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

const { values, errors, status, message, submit, turnstileKey, mountTurnstile } = useSiteForm('quote', {
    siteData: props.site_data,
    fields: {
        name: '', email: '', message: '', project_types: [],
        organisation: '', land_type: '', location: '', length: '', timeline: '', budget: '',
    },
});
const turnstileEl = ref(null);
onMounted(() => { if (turnstileKey) mountTurnstile(turnstileEl.value); });

const types = computed(() => quoteProjectTypes(props.config));
const fields = computed(() => quoteFieldList(props.config));
const steps = computed(() => quoteNextSteps(props.config));
const headingParts = computed(() => accentParts(props.config.heading));
const successText = computed(() => props.config.success_message || message.value);
const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);
</script>

<template>
    <section :id="config.anchor_id || null" class="th-block th-sec th-quote" :style="styleVars(config)">
        <div class="th-wrap">
            <div v-if="config.eyebrow || config.heading || config.body" class="th-quote__head">
                <p v-if="config.eyebrow" class="th-eyebrow">{{ config.eyebrow }}</p>
                <h2 v-if="config.heading" class="th-h2">
                    <template v-for="(part, i) in headingParts" :key="i">
                        <span v-if="part.accent" class="th-accent-word">{{ part.text }}</span>
                        <template v-else>{{ part.text }}</template>
                    </template>
                </h2>
                <p v-if="config.body" class="th-lede th-quote__body">{{ config.body }}</p>
            </div>

            <div class="th-quote__grid" :class="{ 'th-quote__grid--solo': !steps.length }">
                <div v-if="status === 'sent'" class="th-card th-card--pad">
                    <div class="th-form-note th-form-note--ok" role="status">{{ successText }}</div>
                </div>
                <form v-else class="th-card th-stack th-quote__form" novalidate @submit.prevent="submit()">
                    <div v-if="types.length" class="th-stack th-quote__types">
                        <span class="th-quote__label">What are you planning?</span>
                        <div class="th-chips">
                            <label v-for="t in types" :key="t" class="th-check th-quote-type">
                                <input v-model="values.project_types" type="checkbox" :value="t">{{ t }}
                            </label>
                        </div>
                    </div>
                    <div class="th-quote__fields">
                        <label class="th-label">Name
                            <input v-model="values.name" class="th-input th-quote-name" type="text" name="name" autocomplete="name">
                            <span v-if="errors.name" class="th-field-error">{{ errors.name }}</span>
                        </label>
                        <label class="th-label">Email
                            <input v-model="values.email" class="th-input th-quote-email" type="email" name="email" autocomplete="email">
                            <EmailTypoHint :email="values.email" @fix="values.email = $event" />
                            <span v-if="errors.email" class="th-field-error">{{ errors.email }}</span>
                        </label>
                        <label v-for="f in fields" :key="f.key" class="th-label">{{ f.label }}
                            <select v-if="f.options" v-model="values[f.key]" class="th-input" :class="`th-quote-${f.key}`" :name="f.key">
                                <option value="">Choose…</option>
                                <option v-for="o in f.options" :key="o" :value="o">{{ o }}</option>
                            </select>
                            <input v-else v-model="values[f.key]" class="th-input" :class="`th-quote-${f.key}`" type="text" :name="f.key" :placeholder="f.placeholder">
                            <span v-if="errors[f.key]" class="th-field-error">{{ errors[f.key] }}</span>
                        </label>
                    </div>
                    <label class="th-label">Anything else
                        <textarea v-model="values.message" class="th-input th-quote-message" name="message" rows="4"
                                  placeholder="Terrain, existing trails, permits, who uses it"></textarea>
                        <span v-if="errors.message" class="th-field-error">{{ errors.message }}</span>
                    </label>
                    <input v-model="values.website" class="th-hp" type="text" name="website" tabindex="-1" autocomplete="off" aria-hidden="true">
                    <div v-if="turnstileKey" ref="turnstileEl"></div>
                    <div v-if="status === 'error' && message" class="th-form-note th-form-note--error" role="alert">{{ message }}</div>
                    <button type="submit" class="th-btn th-btn--primary th-quote__submit th-quote-submit" :disabled="status === 'sending'">
                        {{ status === 'sending' ? 'Sending…' : (config.button_label || 'Send request') }}
                    </button>
                </form>

                <div v-if="steps.length" class="th-quote__next">
                    <h3 class="th-h2 th-h2--sm">{{ config.next_heading || 'What happens next' }}</h3>
                    <ol class="th-quote__steps">
                        <li v-for="(s, i) in steps" :key="i" class="th-quote__step">
                            <span class="th-quote__num">{{ pad2(i + 1) }}</span>
                            <span>
                                <strong class="th-quote__step-title">{{ s.title }}</strong>
                                <span v-if="s.text" class="th-small th-quote__step-text">{{ s.text }}</span>
                            </span>
                        </li>
                    </ol>
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
.th-quote__head { max-width: 60ch; margin-bottom: clamp(24px, 4vw, 40px); }
.th-quote__body { margin-top: 14px; }
.th-quote__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 380px), 1fr));
    gap: clamp(28px, 4vw, 56px);
    align-items: start;
}
.th-quote__grid--solo { grid-template-columns: minmax(0, 760px); }
.th-quote__form { padding: clamp(20px, 3vw, 32px); --th-gap: 18px; }
.th-quote__types { --th-gap: 8px; }
.th-quote__label { font-size: 14px; font-weight: 600; }
.th-quote__fields { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 14px; }
.th-quote__submit { align-self: flex-start; }
.th-quote__steps { list-style: none; margin: 20px 0 0; padding: 0; }
.th-quote__step {
    display: grid;
    grid-template-columns: 44px minmax(0, 1fr);
    gap: 12px;
    padding: 14px 0;
    border-top: 1px solid var(--th-line);
}
.th-quote__step:last-child { border-bottom: 1px solid var(--th-line); }
.th-quote__num { font: 500 12px/1.6 var(--th-mono); letter-spacing: .1em; color: var(--accent); }
.th-quote__step-title { display: block; font-weight: 600; margin-bottom: 2px; }
.th-quote__step-text { display: block; }
</style>
